import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import "./payment-handler.css";
import Lottie from 'lottie-react';
import { toast } from "react-toastify";
import animate1 from '../../utils/order_placed_back_animation.json';
import animate2 from '../../utils/order_success_tick_animation.json';
import NoOrderSVG from "../../utils/zero-state-screens/No_Orders.svg";
import animateError from '../../utils/error.json';
import api from '../../api/api';
import { setCart, setCartCheckout, setCartProducts, setCartSubTotal } from '../../model/reducer/cartReducer';
import { useDispatch, useSelector } from 'react-redux';

const PayPalPaymentHandler = () => {

    const location = useLocation();
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const user = useSelector(state => state?.user);
    const queryParams = new URLSearchParams(location.search);
    const queryParamsObj = {};
    for (const [key, value] of queryParams.entries()) {
        queryParamsObj[key] = value;
    }
    const [isOrderPayment, setIsOrderPayment] = useState(false);
    const [paymentStatus, setPaymentStatus] = useState(null); // 'success', 'error', or null
    const [errorType, setErrorType] = useState(''); // 'wallet', 'order', or 'unknown'
    const [timer, setTimer] = useState(5);
    const interval = useRef();
    const timeout = useRef();

    useEffect(() => {
        if (queryParams.size === 0) {
            // No query parameters, do nothing or handle as needed
            return;
        }
    
        if (queryParamsObj.type === "wallet") {
            if (queryParamsObj.status === "failed") {
                setPaymentStatus('error');
                setErrorType('wallet');
                toast.error("Wallet payment failed");
            } else if (queryParamsObj.status === "PAYMENT_SUCCESS" || queryParamsObj.status === "success" || queryParamsObj.status === "Paid" || queryParamsObj.status_code == 200) {
                setPaymentStatus('success');
                // Optionally, send a message to the opener window
                window.opener.postMessage("Recharge Done", process.env.REACT_APP_BASE_URL);
            } else if (queryParamsObj.status === "") {
                setPaymentStatus('error');
                setErrorType('wallet');
                toast.error("Something went wrong with the wallet payment");
            }
        } else {
            const orderId = queryParamsObj.payment_method === "Paytabs" ? queryParamsObj.order_id : queryParamsObj.order_id?.split("-")[1];
            if (queryParamsObj.status === "failed") {
                setPaymentStatus('error');
                setErrorType('order');
                toast.error("Order payment failed");
                api.deleteOrder(user?.jwtToken, orderId).then((res) => res.json().then((result) => {
                    // Handle API response if needed
                })).catch((err) => {
                    console.log("Error deleting order:", err);
                });
            } else if (queryParamsObj.status === "") {
                setPaymentStatus('error');
                setErrorType('order');
                api.deleteOrder(user?.jwtToken, orderId).then((res) => res.json().then((result) => {
                    // Handle API response if needed
                })).catch((err) => {
                    console.log("Error deleting order:", err);
                });
                toast.error("Something went wrong with the order payment");
            } else if (queryParamsObj.status_code === 201 && queryParamsObj.action === "back") {
                setPaymentStatus('error');
                setErrorType('order');
                toast.error("Order payment cancelled");
                api.deleteOrder(user?.jwtToken, orderId).then((res) => res.json().then((result) => {
                    // Handle API response if needed
                })).catch((err) => {
                    console.log("Error deleting order:", err);
                });
            }
            else {
                setPaymentStatus('success');
                try {
                    api.removeCart(user?.jwtToken).then((res) => res.json().then((result) => {
                        if (result?.status === 1) {
                            dispatch(setCart({ data: null }));
                            dispatch(setCartCheckout({ data: null }));
                            dispatch(setCartProducts({ data: [] }));
                            dispatch(setCartSubTotal({ data: 0 }));
                        }
                    }));
                } catch (err) {
                    console.log(err.message);
                }
            }
        }
    
        // Set up a timeout to navigate away after a delay
        const timeoutId = setTimeout(() => {
            setPaymentStatus(null);
            setErrorType('');
            navigate("/");
        }, 60000);
    
        return () => {
            clearTimeout(timeoutId);
        };
    }, [queryParams.size, queryParamsObj.type, queryParamsObj.status, queryParamsObj.status_code, queryParamsObj.action, user?.jwtToken, navigate]);

    if (queryParams.size === 0) {
        return (
            <div className='container d-flex flex-column align-items-center mt-5 payment-container'>
                <img src={NoOrderSVG} alt="noOrderSVG" />
            </div>
        );
    }

    const handleNavigate = () => {
        navigate("/");
    };

    return (
        <>
            {paymentStatus === 'error' ? (
                <div className='container d-flex flex-column align-items-center mt-5 payment-container'>
                    <Lottie animationData={animateError} loop={false} className='lottie-tick' />
                    <div className='text-center'>
                        {errorType === 'wallet' && <p className='error-desc'>{t("wallet_payment_failed")}</p>}
                        {errorType === 'order' && <p className='error-desc'>{t("order_payment_failed")}</p>}
                        {errorType === 'unknown' && <p className='error-desc'>{t("unknown_error")}</p>}
                    </div>
                    <button onClick={handleNavigate} className='checkout_btn'>
                        {t("go_to_home")}
                    </button>
                </div>
            ) : paymentStatus === 'success' ? (
                <div className='container d-flex flex-column align-items-center mt-5 payment-container'>
                    <Lottie animationData={animate2} loop={false} className='lottie-tick' />
                    <Lottie className='lottie-content' animationData={animate1} loop={true} />
                    <div className='text-center'>
                        {queryParamsObj.type === "wallet" ? (
                            <p className='order-success-desc'>{t("wallet_payment_success_description")}</p>
                        ) : (
                            <p className='order-success-desc'>{t("order_placed_description")}</p>
                        )}
                    </div>
                    <button onClick={handleNavigate} className='checkout_btn'>
                        {t("go_to_home")}
                    </button>
                </div>
            ) : (
                // Default Rendering (e.g., loading or no orders)
                <div className='container d-flex flex-column align-items-center mt-5 payment-container'>
                    <img src={NoOrderSVG} alt="noOrderSVG" />
                </div>
            )}
        </>
    );
};

export default PayPalPaymentHandler;